import Plpurl from '@tvg/product-listing-url';
import { PageType } from '@tvg/product-listing-url/lib/Types/types';
import { DEFAULT_PRODUCTS_PER_PAGE } from 'common/config';
import { PageTypeFilterMap, SortOption, SortOptionsMap } from './consts';

const AMPERSAND_TEMP_REPLACEMENT = '@&@';
const AMPERSAND_REGEX = new RegExp(AMPERSAND_TEMP_REPLACEMENT, 'g');

const PLUS_TEMP_REPLACEMENT = '@+@';
const PLUS_REGEX = /@\+@/g; // need to escape the + as it is a regex special char

const SLASH_TEMP_REPLACEMENT = '@/@';
const SLASH_REGEX = new RegExp(SLASH_TEMP_REPLACEMENT, 'g');

function replaceSpecialChars(text: string) {
  return text
    .replace(/&/g, AMPERSAND_TEMP_REPLACEMENT)
    .replace(/\+/g, PLUS_TEMP_REPLACEMENT)
    .replace(/\//g, SLASH_TEMP_REPLACEMENT);
}

function encodeSpecialChars(text: string) {
  return text.replace(AMPERSAND_REGEX, '%26').replace(PLUS_REGEX, '%2B').replace(SLASH_REGEX, '%2F');
}

const getFilterName = (type: PageType) => PageTypeFilterMap[type];

const getFilterValue = (type: PageType, plpurl: Plpurl) => {
  if (type === PageType.browse) return plpurl.category?.id;
  if (type === PageType.promo) return plpurl.pageName;
  if (type === PageType.valuedeal) return plpurl.pageName;
  return plpurl.searchTerm;
};

const getGroupId = (plpurl: Plpurl) => {
  if (!plpurl.category?.id) return;
  if (plpurl.type === PageType.browse && !plpurl.unfilteredFacet) return;
  if (plpurl.type !== PageType.browse && plpurl.category.id === 'all') return;

  // eslint-disable-next-line consistent-return
  return replaceSpecialChars(plpurl.category.id);
};

export function buildAPIUrl(plpurl: Plpurl, apiURL: string, customerID?: string | undefined) {
  if (!plpurl.type) throw new Error('missing_page_type');

  const filters: string[] = [];

  let unfilteredFacetPath;

  plpurl.facets?.forEach((facet) => {
    facet.values?.forEach((facetValue) => {
      const facetValueId = facetValue?.id || '';
      const facetId =
        plpurl.type !== PageType.browse ? facet.id : facet?.id?.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
      if (plpurl.type === PageType.browse && facet.id === plpurl?.unfilteredFacet) {
        unfilteredFacetPath = `${replaceSpecialChars(facetId || '')}/${replaceSpecialChars(facetValueId)}`;
      } else {
        filters.push(`filters[${replaceSpecialChars(facetId || '')}]=${replaceSpecialChars(facetValueId)}`);
      }
    });
  });

  const groupId = getGroupId(plpurl);
  if (groupId) filters.push(`filters[group_id]=${groupId}`);

  const facetParams = filters.length ? `${filters.join('&')}` : '';

  let sortOption = '';
  if (plpurl.sortOption) {
    const mappedSortOption = SortOptionsMap[plpurl.sortOption as SortOption];
    if (mappedSortOption) {
      sortOption = `&sort_by=${mappedSortOption[0]}&sort_order=${mappedSortOption[1]}`;
    }
  }

  let params = `${facetParams + (facetParams === '' ? '' : '&')}num_results_per_page=${
    plpurl.numProducts || DEFAULT_PRODUCTS_PER_PAGE
  }&page=${plpurl.pageNumber || 1}${sortOption}`;

  if (typeof window !== 'undefined') {
    if (window.cnstrcUserId) {
      params += `&ui=${window.cnstrcUserId}`;
    }
  } else if (customerID) {
    params += `&ui=${customerID}`;
  }

  const separator = apiURL.endsWith('/') ? '' : '/';

  if (unfilteredFacetPath) {
    return encodeSpecialChars(encodeURI(`${apiURL}${separator}${unfilteredFacetPath}?${params}`));
  }

  const filterName = getFilterName(plpurl.type);
  const filterValue = getFilterValue(plpurl.type, plpurl);
  const filter = [...(!filterName ? [] : [filterName]), filterValue].join('/');

  return encodeSpecialChars(encodeURI(`${apiURL}${separator}${filter}?${params}`));
}
